import { useEffect, useContext, useRef } from 'react';
import { SimulationContext } from 'contexts/SimulationContext';

import WindSpeedScalar from './WindSpeedScalar';

import TextField from '@mui/material/TextField';
import Autocomplete from '@mui/material/Autocomplete';
import Box from '@mui/material/Box';
import Slider from '@mui/material/Slider';
import Typography from '@mui/material/Typography';
import { DeleteIcon } from 'components/icons';
import IconButton from '@mui/material/IconButton';
import { Grid, TextInput } from 'components/lib';
import Divider from '@mui/material/Divider';
import MuiGrid from '@mui/material/Grid';

import '../styles/simulation.css';

import { windSpeedScalars } from 'data/windSpeedScalars';
import CustomTooltip from 'components/tooltip/CustomTooltip';
import { ClosestStationContext, type WeatherStation } from 'contexts/ClosestStationContext';
import WindSpeedScalarTable from './WindSpeedScalarTable';
import { hydrateWindSubSimulationsWithStation } from 'services/windParamsHydration';
import { UMButton, UMInputNumber } from '@urbanmetrix/um-kit';
import { GeodataContext } from 'contexts/GeodataContext';
import {
  MAX_WIND_AVERAGE_BUILDING_HEIGHT,
  MAX_WIND_SENSOR_HEIGHT_FROM_GROUND,
  MAX_WIND_SPEED,
} from 'constants/simulation';
import { getInputNumberRangeErrorMessage, isInputNumberInRange } from 'utils/inputNumberErrorMessage';

const landscapeRoughness = [
  {
    amount: 0.03,
    name: '0.03 - Flat terrain with grass, very low vegetation, very scattered buildings, softly rounded hills',
  },
  {
    amount: 0.1,
    name: '0.1 - Cultivated area, landscape with some houses of height H separated by at least 20 H',
  },
  {
    amount: 0.4,
    name: '0.4 - Villages, small towns, landscape with bushes, young forests, rough and uneven terrain',
  },
  {
    amount: 0.8,
    name: '0.8 - Cities with low and mid-rise buildings, mature forests',
  },
  {
    amount: 1.6,
    name: '1.6 - Large cities with tall buildings',
  },
  {
    amount: 2.0,
    name: '2.0 - City center with tall buildings, large forest with clearings',
  },
];

const getLandscapeRoughness = (value: unknown) => {
  const roughness = Number(value);
  return (
    landscapeRoughness.find((option) => Number.isFinite(roughness) && Math.abs(option.amount - roughness) < 0.001) ??
    landscapeRoughness[2]
  );
};

interface WindProps {
  isTheAreaInSwitzerland: boolean;
}

/** Coerce a loosely-typed param value to what UMInputNumber's `value` prop accepts. */
const toInputValue = (value: unknown): number | string | null => {
  if (value == null) return null;
  if (typeof value === 'number' || typeof value === 'string') return value;
  return null;
};

const Wind = ({ isTheAreaInSwitzerland }: WindProps) => {
  const {
    simulation,
    setSimulation,
    subSimulations,
    setSubSimulations,
    setSubSimulationParams,
    setSubSimulationName,
    defaultWind,
    isCloningSimulation,
  } = useContext(SimulationContext);
  const { closestStation, setClosestStation, loadingClosestStation, initialClosestStation } =
    useContext(ClosestStationContext);
  const { geodataParams } = useContext(GeodataContext);
  const lengthMidTerrain = Number(geodataParams.lengthMid);
  const lengthFarTerrain = Number(geodataParams.lengthFar);
  const defaultMeshDistance: number | null = 500;
  const maxMeshDistance =
    Number.isFinite(lengthMidTerrain) && Number.isFinite(lengthFarTerrain)
      ? lengthMidTerrain + lengthFarTerrain
      : undefined;
  const previousClosestStationRef = useRef<WeatherStation | null>(null);

  /**
   * Updates windHeightFromTheGround and first default wind simulation in context when closestStation changes
   */
  useEffect(() => {
    updateToClosestStation();
  }, [closestStation]);

  useEffect(() => {
    if (!Number.isFinite(lengthMidTerrain)) return;

    const nextSubSimulations = subSimulations.map((subSim) => {
      if (subSim?.simType !== 'WIND') return subSim;

      const rawMeshDistance = subSim?.params?.meshDistance;
      const meshDistance = rawMeshDistance === '' || rawMeshDistance == null ? NaN : Number(rawMeshDistance);
      if (Number.isFinite(meshDistance)) return subSim;

      return {
        ...subSim,
        params: {
          ...subSim.params,
          meshDistance: defaultMeshDistance,
        },
      };
    });

    const hasChanges = nextSubSimulations.some((subSim, index) => subSim !== subSimulations[index]);
    if (hasChanges) {
      setSubSimulations(nextSubSimulations);
    }
  }, [defaultMeshDistance, lengthMidTerrain, setSubSimulations, subSimulations]);

  const updateToClosestStation = () => {
    if (closestStation && !isCloningSimulation) {
      const subSimulations = hydrateWindSubSimulationsWithStation(
        simulation.subSimulations,
        closestStation,
        previousClosestStationRef.current
      );

      setSimulation((prevSimulation) => {
        return {
          ...prevSimulation,
          subSimulations: subSimulations,
        };
      });
    }

    previousClosestStationRef.current = closestStation;
  };
  /**
   * Basic input changes
   */
  const handleInputsChange = (name: string | undefined, value: unknown, id: number) => {
    if (!name) return;
    setSubSimulationParams(id, { [name]: value });
  };

  const handleNameChange = (newName: string, id: number) => {
    setSubSimulationName(newName, id);
  };

  /**
   * Slider simulation accuracy
   */
  const handleChangeSliderSimulationAccuracy = (value: number | number[], id: number) => {
    const numericValue = Array.isArray(value) ? value[0] : value;
    let simulationAccuracy = 'MEDIUM';

    if (numericValue <= 25) {
      simulationAccuracy = 'LOW';
    } else if (numericValue >= 75) {
      simulationAccuracy = 'HIGH';
    }

    handleInputsChange('windSimulationAccuracy', simulationAccuracy, id);
  };

  const resolveSimulationAccuracyValue = (accuracy: unknown) => {
    if (accuracy === 'LOW') return 0;
    if (accuracy === 'HIGH') return 100;
    return 50;
  };

  /**
   * Dropdown change closest station
   */
  const handleChangeStation = (_e: React.SyntheticEvent, value: WeatherStation | null) => {
    // When the cross icon is clicked
    if (value === null || value === undefined) {
      setClosestStation(initialClosestStation);
    } else {
      setClosestStation(value);
    }
  };
  const addWindSimulation = () => {
    const newId =
      subSimulations.length > 0 ? Math.max(...subSimulations.map((subSimulation) => subSimulation.id)) + 1 : 0;
    setSubSimulations([...subSimulations, defaultWind(newId, closestStation, defaultMeshDistance)]);
  };

  const removeBlock = (id: number) => {
    setSubSimulations(subSimulations.filter((block) => block.id !== id));
  };

  return (
    <>
      <Typography variant="subtitle2" sx={{ fontWeight: 550, marginBottom: 2 }}>
        Observe the patterns in the 3 wind rose pages and decide how many simulations to run. By default we set one with
        the Main wind pattern of the closest meteo station.
      </Typography>

      <MuiGrid container spacing={2}>
        <MuiGrid size={{ lg: 10 }}>
          <Typography variant="subtitle2">
            The closest wind measurement station is: {initialClosestStation?.station}
          </Typography>
          <br />
          {!loadingClosestStation && (
            <Autocomplete
              disablePortal
              options={windSpeedScalars}
              getOptionLabel={(option) => option?.station}
              renderInput={(params) => <TextField {...params} label="Select another station" />}
              onChange={handleChangeStation}
              defaultValue={closestStation}
              value={closestStation}
              // sx={{ width: 400 }}
            />
          )}
          <br />
          <Typography variant="subtitle1">Monthly and Average Wind Speed of this station:</Typography>
          <WindSpeedScalarTable />
        </MuiGrid>
        <MuiGrid size={{ lg: 2 }}>{isTheAreaInSwitzerland && <WindSpeedScalar />}</MuiGrid>
      </MuiGrid>

      <hr />
      <br />
      {subSimulations
        .filter((subSim) => subSim.simType === 'WIND')
        .map((subSimulation, index) => (
          <div key={index}>
            <Grid cols="2">
              <TextInput
                label="Wind Simulation Name"
                name={`name`}
                value={subSimulation?.name || ''}
                onChange={(_name, value) => handleNameChange(value, subSimulation.id)}
              />
              <div style={{ textAlign: 'right' }}>
                <IconButton size="large" color="error" onClick={() => removeBlock(subSimulation.id)}>
                  <DeleteIcon />
                </IconButton>
              </div>
            </Grid>
            <Grid cols="2">
              <UMInputNumber
                label="Average Speed of the wind [m/s]"
                name={`referenceVelocity`}
                width={84}
                maxErrorMessageWidth={276}
                min={0}
                max={MAX_WIND_SPEED}
                valid={isInputNumberInRange(subSimulation.params?.referenceVelocity, 0, MAX_WIND_SPEED)}
                errorMessage={getInputNumberRangeErrorMessage(0, MAX_WIND_SPEED)}
                value={toInputValue(subSimulation.params?.referenceVelocity) ?? closestStation?.avgYear ?? ''}
                defaultValue={closestStation?.avgYear}
                onChange={(name, value) => handleInputsChange(name, value, subSimulation.id)}
              />
              <UMInputNumber
                label={
                  <span className="grid-inline-label-with-info">
                    <span>Direction from which the wind is blowing</span>
                    <CustomTooltip title="° Counter-Clock-Wise from East" />
                  </span>
                }
                name={`flowDir`}
                width={84}
                maxErrorMessageWidth={276}
                value={toInputValue(subSimulation?.params.flowDir) ?? closestStation?.avgDir ?? ''}
                min={0}
                max={360}
                valid={isInputNumberInRange(subSimulation.params?.flowDir, 0, 360)}
                errorMessage={getInputNumberRangeErrorMessage(0, 360)}
                onChange={(name, value) => handleInputsChange(name, value, subSimulation.id)}
                defaultValue={closestStation?.avgDir}
              />
            </Grid>

            <Grid cols="1">
              <br />
              <Autocomplete
                disablePortal
                options={landscapeRoughness}
                getOptionLabel={(option) => option.name}
                renderInput={(params) => <TextField {...params} label="Select a landscape roughness *" />}
                value={getLandscapeRoughness(subSimulation.params?.surfaceRoughness)}
                onChange={(_e, selectedOption) => {
                  handleInputsChange('surfaceRoughness', selectedOption?.amount ?? '', subSimulation.id);
                }}
              />
            </Grid>

            <Grid cols="3">
              <UMInputNumber
                label={
                  <span className="grid-inline-label-with-info">
                    <span>Radius of the Wind Analysis [m]</span>
                    <CustomTooltip title={`Default value is Intermediate length + 100m (${defaultMeshDistance}m).`} />
                  </span>
                }
                name="meshDistance"
                width={84}
                maxErrorMessageWidth={276}
                min={defaultMeshDistance ?? undefined}
                value={toInputValue(subSimulation.params?.meshDistance) ?? ''}
                defaultValue={defaultMeshDistance ?? undefined}
                max={maxMeshDistance}
                valid={isInputNumberInRange(
                  subSimulation.params?.meshDistance,
                  defaultMeshDistance ?? undefined,
                  maxMeshDistance
                )}
                errorMessage={getInputNumberRangeErrorMessage(defaultMeshDistance ?? undefined, maxMeshDistance)}
                onChange={(name, value) => {
                  handleInputsChange(name, value, subSimulation.id);
                }}
              />

              <UMInputNumber
                label="Average building height in this area [m]"
                name="windAverageBuildingHeight"
                width={84}
                maxErrorMessageWidth={276}
                min={0}
                max={MAX_WIND_AVERAGE_BUILDING_HEIGHT}
                valid={isInputNumberInRange(
                  subSimulation.params?.windAverageBuildingHeight,
                  0,
                  MAX_WIND_AVERAGE_BUILDING_HEIGHT
                )}
                errorMessage={getInputNumberRangeErrorMessage(0, MAX_WIND_AVERAGE_BUILDING_HEIGHT)}
                value={toInputValue(subSimulation.params?.windAverageBuildingHeight)}
                defaultValue={0}
                onChange={(name, value) => {
                  handleInputsChange(name, value, subSimulation.id);
                }}
              />

              <UMInputNumber
                label={
                  <span className="grid-inline-label-with-info">
                    <span>Wind Sensor height from ground [m]</span>
                    <CustomTooltip title={'Height from ground of the wind measurement sensor.'} />
                  </span>
                }
                width={84}
                maxErrorMessageWidth={276}
                min={0}
                max={MAX_WIND_SENSOR_HEIGHT_FROM_GROUND}
                name="referenceHeight"
                valid={isInputNumberInRange(
                  subSimulation.params?.referenceHeight,
                  0,
                  MAX_WIND_SENSOR_HEIGHT_FROM_GROUND
                )}
                errorMessage={getInputNumberRangeErrorMessage(0, MAX_WIND_SENSOR_HEIGHT_FROM_GROUND)}
                value={toInputValue(subSimulation.params?.referenceHeight) ?? closestStation?.measurementHeight ?? ''}
                defaultValue={closestStation?.measurementHeight}
                onChange={(name, value) => {
                  handleInputsChange(name, value, subSimulation.id);
                }}
              />
            </Grid>

            <br />

            <Grid cols="3">
              <Box>
                <Typography variant="caption">
                  <span className="grid-inline-label-with-info">
                    <span>Simulation accuracy</span>
                    <CustomTooltip title={'Resolution of the computation.'} />
                  </span>
                </Typography>
                <br />
                <Slider
                  sx={{ maxWidth: '400px', marginLeft: '15px' }}
                  onChange={(_, value) => handleChangeSliderSimulationAccuracy(value, subSimulation.id)}
                  step={null}
                  value={resolveSimulationAccuracyValue(subSimulation.params?.windSimulationAccuracy)}
                  marks={[
                    {
                      value: 0,
                      label: 'Low',
                    },
                    {
                      value: 50,
                      label: 'Medium',
                    },
                    {
                      value: 100,
                      label: 'High',
                    },
                  ]}
                />
              </Box>
            </Grid>

            <br />

            <Divider />
            <br />
          </div>
        ))}
      <UMButton text="Add sim" onClick={addWindSimulation} className="glass-btn-1" />
    </>
  );
};

export default Wind;
